import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Reply, Pencil, Trash2, Shield, Check, X, Pin } from 'lucide-react';
import { RankChip } from '../../RankBadge';
import { Composer } from './Composer';
import { ReactionBar, ReactionPicker } from './Reactions';
import { formatTime, formatFullDate, tokenizeMentions } from './chatUtils';

export const MentionText = ({ content, mentions, mentionsAll, meId }) => (
  <>
    {tokenizeMentions(content, mentions, mentionsAll).map((p, i) => p.mention ? (
      <span key={i} className={`font-mono text-xs px-1.5 py-0.5 rounded border inline-flex items-center ${p.mention.id === meId || p.mention.id === '*' ? 'bg-amber-500/25 text-amber-300 border-amber-400/50' : 'bg-amber-500/15 text-amber-400 border-amber-500/30'}`} data-testid="mention-chip">{p.text}</span>
    ) : <span key={i}>{p.text}</span>)}
  </>
);

export const MessageRow = ({
  msg, grouped, me, canDelete, canPin = false, isEditing, isReplying, members, flash = false,
  onReply, onEdit, onDelete, onSaveEdit, onCancelEdit, onSendReply, onCancelReply, onReact, onPin, onTyping, onOpenImage, onUploadImage = null, everyoneLabel = null,
  allowMentions = true, maxLength = 500, renderMedia = null, ownerLabel = 'Owner'
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isOwn = msg.senderId === me.id;
  const mentionsMe = (msg.mentions || []).some((m) => m.id === me.id) || (msg.mentionsAll && !isOwn);
  const sender = msg.sender || {};

  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}
      className={`group relative px-4 ${grouped ? 'py-0.5' : 'pt-2.5 pb-0.5'} hover:bg-zinc-800/30 transition-colors duration-500 flex gap-3 items-start ${mentionsMe ? 'bg-amber-950/20 border-l-2 border-amber-500 pl-3.5' : ''} ${isReplying ? 'bg-zinc-800/20' : ''} ${flash ? 'bg-amber-500/25' : ''}`}
      data-testid={`chat-message-${msg.id}`}>
      {grouped ? (
        <span className="w-8 flex-shrink-0 text-[10px] font-typewriter text-zinc-600 opacity-0 group-hover:opacity-100 text-right pt-1 leading-none" title={formatFullDate(msg.createdAt)}>{formatTime(msg.createdAt)}</span>
      ) : (
        <Avatar className="w-8 h-8 rounded-full border border-zinc-700/80 flex-shrink-0 mt-0.5">
          <AvatarImage src={sender.avatarUrl} />
          <AvatarFallback className="bg-zinc-800 text-amber-500 text-xs">{sender.displayName?.charAt(0) || '?'}</AvatarFallback>
        </Avatar>
      )}

      <div className="flex-1 min-w-0">
        {!grouped && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-headline text-sm tracking-wide ${sender.role === 'owner' ? 'text-red-400' : isOwn ? 'text-amber-400' : 'text-zinc-200'}`}>{sender.displayName || 'Unknown'}</span>
            {sender.role === 'owner' && <span className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded tracking-widest uppercase">{ownerLabel}</span>}
            {sender.role === 'moderator' && <Shield className="text-amber-400 w-3.5 h-3.5" title="Moderator" />}
            <RankChip rank={sender.rank} />
            <span className="font-typewriter text-[10px] text-zinc-500" title={formatFullDate(msg.createdAt)} data-testid="message-time">{formatTime(msg.createdAt)}</span>
            {msg.pinned && <span className="flex items-center gap-0.5 text-[10px] font-typewriter text-amber-500" data-testid="pinned-label"><Pin className="w-3 h-3" />pinned</span>}
          </div>
        )}

        {msg.replyTo && (
          <div className="border-l-2 border-amber-500/60 pl-2 py-1 text-xs text-zinc-400 font-typewriter bg-zinc-900/50 rounded-r mb-1 mt-0.5 max-w-[90%] min-w-0">
            <span className="text-amber-500/90">{msg.replyTo.senderName}</span>
            <p className="truncate [overflow-wrap:anywhere]">{msg.replyTo.content}</p>
          </div>
        )}

        {msg.imageUrl && !isEditing && (
          <button type="button" onClick={() => onOpenImage(msg.imageUrl)} className="block mt-1.5 mb-1 rounded-lg overflow-hidden border border-zinc-700/80 hover:border-amber-500/60 transition-colors cursor-zoom-in max-w-[320px]" data-testid={`message-image-${msg.id}`}>
            <img src={/\.gif(\?|$)/i.test(msg.imageUrl) ? msg.imageUrl : (msg.imageThumbUrl || msg.imageUrl)} alt="" loading="lazy" className="max-h-60 w-auto object-cover" data-testid={`message-image-src-${msg.id}`} />
          </button>
        )}

        {isEditing ? (
          <div className="mt-1" data-testid={`edit-composer-${msg.id}`}>
            <Composer initialValue={msg.content} initialMentions={msg.mentions || []} members={members} compact autoFocus allowMentions={allowMentions} maxLength={maxLength}
              submitLabel="Save" placeholder={msg.imageUrl ? 'Add a caption…' : 'Edit your message…'} testId="chat-edit-input"
              onSubmit={(text, mentionIds) => onSaveEdit(msg.id, text, mentionIds)} onCancel={onCancelEdit} />
          </div>
        ) : renderMedia && renderMedia(msg) ? (
          <div className="mt-1 max-w-full">{renderMedia(msg)}{msg.editedAt && <span className="font-typewriter text-[10px] text-zinc-500 italic" data-testid="edited-label">(edited)</span>}</div>
        ) : msg.content ? (
          <p className="text-sm text-zinc-200 leading-relaxed break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
            <MentionText content={msg.content} mentions={msg.mentions} mentionsAll={msg.mentionsAll} meId={me.id} />
            {msg.editedAt && <span className="font-typewriter text-[10px] text-zinc-500 italic ml-1.5" title={`Edited ${formatFullDate(msg.editedAt)}`} data-testid="edited-label">(edited)</span>}
          </p>
        ) : null}

        {!isEditing && <ReactionBar reactions={msg.reactions} meId={me.id} messageId={msg.id} onToggle={(emoji) => onReact(msg.id, emoji)} />}

        {isReplying && (
          <div className="bg-[#18181b] border-l-2 border-amber-500 p-2.5 my-2 rounded-r-md shadow-inner" data-testid={`inline-reply-${msg.id}`}>
            <Composer members={members} compact autoFocus allowMentions={allowMentions} maxLength={maxLength} submitLabel="Reply" placeholder={`Reply to ${sender.displayName || 'this message'}…`} testId="chat-reply-input"
              replyPreview={{ senderName: sender.displayName || 'Unknown', content: msg.content || (msg.imageUrl ? '📷 Photo' : '') }} onTyping={onTyping} onUploadImage={onUploadImage} everyoneLabel={everyoneLabel}
              onSubmit={(text, mentionIds, image) => onSendReply(msg, text, mentionIds, image)} onCancel={onCancelReply} />
          </div>
        )}
      </div>

      {!isEditing && !isReplying && (
        <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 hover:opacity-100 transition-opacity absolute right-3 -top-3 bg-[#18181b] border border-zinc-700 rounded-md p-0.5 flex items-center gap-0.5 shadow-md z-20 text-zinc-300" data-testid={`message-actions-${msg.id}`}>
          {confirmDelete ? (
            <>
              <span className="text-[10px] font-typewriter text-red-400 px-1.5">Delete?</span>
              <button onClick={() => { onDelete(msg.id); setConfirmDelete(false); }} className="p-1 rounded hover:bg-red-600/30 text-red-400" title="Confirm delete" data-testid={`confirm-delete-${msg.id}`}><Check className="w-3.5 h-3.5" /></button>
              <button onClick={() => setConfirmDelete(false)} className="p-1 rounded hover:bg-zinc-700" title="Keep" data-testid={`cancel-delete-${msg.id}`}><X className="w-3.5 h-3.5" /></button>
            </>
          ) : (
            <>
              <ReactionPicker onPick={(emoji) => onReact(msg.id, emoji)} testId={`react-btn-${msg.id}`} />
              <button onClick={() => onReply(msg)} className="p-1 rounded hover:bg-zinc-700 hover:text-amber-400" title="Reply" data-testid={`reply-btn-${msg.id}`}><Reply className="w-3.5 h-3.5" /></button>
              {isOwn && <button onClick={() => onEdit(msg.id)} className="p-1 rounded hover:bg-zinc-700 hover:text-amber-400" title="Edit" data-testid={`edit-btn-${msg.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
              {canPin && <button onClick={() => onPin(msg.id)} className={`p-1 rounded hover:bg-zinc-700 ${msg.pinned ? 'text-amber-400' : 'hover:text-amber-400'}`} title={msg.pinned ? 'Unpin' : 'Pin to top'} data-testid={`pin-btn-${msg.id}`}><Pin className="w-3.5 h-3.5" /></button>}
              {canDelete && <button onClick={() => setConfirmDelete(true)} className="p-1 rounded hover:bg-zinc-700 hover:text-red-400" title="Delete" data-testid={`delete-btn-${msg.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default MessageRow;
